import { useEffect, useState } from 'react';
import { Wifi, Calendar, Database, Loader2 } from 'lucide-react';
import { useApp } from "@/contexts/AppContext";
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';
import {
  getActiveSubscription,
  cancelSubscription,
  toggleAutoRenew,
  getConnectionHistory,
  type ActiveSubscription,
  // type Connection
} from '@/api/services/subscription';
import FeedbackModal from '@/components/FeedbackModal';
// import ActionConfirmationModal from '@/components/ActionConfirmationModal';







export default function SubscriptionsPage() {
  const { theme } = useApp();
  const { i18n } = useTranslation();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem('i18nextLng')?.startsWith('fr') ? 'fr' : 'en'
  );
  const t = i18n.getFixedT(langOverride);
  const [activeTab, setActiveTab] = useState('connection-history');
  const [subscription, setSubscription] = useState<ActiveSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
    const [loadingpagination, setloadingPagination] = useState(false);
  const [haspagination, sethasPagination] = useState(true);
const [pagination, setpagination] = useState<unknown>({});
    const [currentpagination, setcurrentpagination] = useState(1);
  
  // Modal states
  const [showFeedback, setShowFeedback] = useState(false);
  // When true, closing the feedback modal reloads the page (e.g. after a
  // successful cancellation) so the subscription view reflects the new state.
  const [reloadOnFeedbackClose, setReloadOnFeedbackClose] = useState(false);
  const [feedbackData, setFeedbackData] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ type: 'success', title: '', message: '' });
  
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [existingHospot, setExistingHotspot] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      const normalized = storedLang.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalized = next?.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
    };
    window.addEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    };
  }, [i18n]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [subResponse, historyResponse] = await Promise.all([
        getActiveSubscription(),
        getConnectionHistory(1, 10)
      ]);
      
      setSubscription(subResponse.data);
      setExistingHotspot(historyResponse.data.connections);
      setpagination(((historyResponse as unknown as Record<string, unknown>)?.data as Record<string, unknown>)?.pagination as Record<string, unknown> || {});
    } catch (err: unknown) {
      console.error('Error loading data:', err);
      setError(String((err as unknown as Record<string, unknown>).message || t('enduser.subscriptions.errorMessages.loadFailed', { defaultValue: 'Failed to load data' })));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    if (!subscription) return;
    
    try {
      setActionLoading(true);
      const newValue = !subscription.autoRenew;
      
      await toggleAutoRenew(subscription.subscriptionId, newValue);
      
      // Update local state
      setSubscription(prev => prev ? { ...prev, autoRenew: newValue } : null);
      
      setFeedbackData({
        type: 'success',
        title: t('enduser.subscriptions.autoRenew.title', { defaultValue: 'Auto-Renew Updated' }),
        message: t('enduser.subscriptions.autoRenew.message', {
          defaultValue: `Auto-renew has been ${newValue ? 'enabled' : 'disabled'} successfully.`,
          status: newValue ? t('enduser.subscriptions.autoRenew.enabled', { defaultValue: 'enabled' }) : t('enduser.subscriptions.autoRenew.disabled', { defaultValue: 'disabled' })
        })
      });
      setShowFeedback(true);
    } catch (err: unknown) {
      console.error('Error toggling auto-renew:', err);
      setFeedbackData({
        type: 'error',
        title: t('enduser.subscriptions.autoRenew.failedTitle', { defaultValue: 'Update Failed' }),
        message: String((err as unknown as Record<string, unknown>).message || t('enduser.subscriptions.autoRenew.failedMessage', { defaultValue: 'Failed to toggle auto-renew. Please try again.' }))
      });
      setShowFeedback(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    if (!subscription) return;
    setShowCancelConfirmation(true);
  };

  const confirmCancelSubscription = async () => {
    if (!subscription) return;
    
    try {
      setActionLoading(true);
      
      const response = await cancelSubscription(
        subscription.subscriptionId, 
        cancelReason || t('enduser.subscriptions.cancel.defaultReason', { defaultValue: 'No reason provided' }), 
        false
      );
      
      // Update local state
      setSubscription(prev => prev ? { ...prev, status: 'cancelled' } : null);
      
      setFeedbackData({
        type: 'success',
        title: t('enduser.subscriptions.cancel.title', { defaultValue: 'Subscription Cancelled' }),
        message: t('enduser.subscriptions.cancel.message', {
          defaultValue: `Your subscription has been cancelled. It will remain active until ${subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'the end of the period'}.`,
          date: subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'the end of the period'
        })
      });
      setShowFeedback(true);
      setShowCancelConfirmation(false);
      setCancelReason('');
      // Reload the page once the user acknowledges the success modal, so the
      // dashboard reflects the cancelled subscription.
      setReloadOnFeedbackClose(true);
    } catch (err: unknown) {
      console.error('Error cancelling subscription:', err);
      setFeedbackData({
        type: 'error',
        title: t('enduser.subscriptions.cancel.failedTitle', { defaultValue: 'Cancellation Failed' }),
        message: String((err as unknown as Record<string, unknown>).message || t('enduser.subscriptions.cancel.failedMessage', { defaultValue: 'Failed to cancel subscription. Please try again.' }))
      });
      setShowFeedback(true);
    } finally {
      setActionLoading(false);
    }
  };

    
  const nextpagination = async (page, limit)=>{
      setloadingPagination(true);
    
    try{

      let response = await getConnectionHistory(page, limit);
  
      setExistingHotspot((prev)=> {
        if(prev !==  null){
          let ret =  [...prev];
          let ret1 =  response.data.connections;
          console.log(ret, response.data.connections, "connections")
          return [...ret,...ret1]
  
        }
  
  });
      setpagination(((response as unknown as Record<string, unknown>)?.data as Record<string, unknown>)?.pagination as Record<string, unknown> || {});
    } catch (err: unknown){
      console.log(err);
    } finally{
      setloadingPagination(false);
    }


  };

  const paginationFunc = async () => {
    console.log(pagination);
    if ((pagination as Record<string, unknown>)?.totalPages > (pagination as Record<string, unknown>)?.page) {
      sethasPagination(true);
      nextpagination(currentpagination + 1, 10);
      setcurrentpagination((prev) => prev + 1);
    }
     else  {
      sethasPagination(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('enduser.subscriptions.metaTitle', { defaultValue: 'Subscriptions | KonectX' })}</title>
        <meta
          name="description"
          content={t('enduser.subscriptions.metaDescription', { defaultValue: 'Manage your subscriptions within the KonectX network.' })}
        />
      </Helmet>

      <div className="p-4 sm:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {t('enduser.subscriptions.headertitle', { defaultValue: 'Subscriptions' })}
          </h1>
          <p className={`font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('enduser.subscriptions.headerbody1', { defaultValue: 'Manage your active plans and subscription details' })}
          </p>
        </div>

        {/* Active Subscription Card */}
        {subscription ? (
          <div className={`${theme === 'dark' ? 'bg-[#333436]' : 'bg-white'} rounded-xl p-4 sm:p-8 shadow-sm border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'} mb-8`}>
            <div className="mb-6">
              <h2 className={`text-lg font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.subscriptions.active.title', { defaultValue: 'Active Subscription' })}
              </h2>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Your current plan details
              </p>
            </div>

            <div className="mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 mb-4">
                <div>
                  <h3 className={`text-2xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {subscription.plan.name}
                  </h3>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Started {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : "N/A"}
                  </p>
                </div>
                <span className={`w-full sm:w-auto text-center sm:text-left px-4 py-2 rounded-lg text-sm font-bold ${
                  subscription.status === 'active' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>

              {/* Data Usage */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-bold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Data Usage
                  </span>
                  <span className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {(subscription.dataUsage as Record<string, unknown>).isUnlimited || String(subscription.dataUsage.total) === 'Unlimited'
                      ? `${subscription.dataUsage.used} GB used \u00B7 Unlimited`
                      : `${subscription.dataUsage.used} / ${subscription.dataUsage.total} ${subscription.dataUsage.unit}`}
                  </span>
                </div>
                <div className={`w-full rounded-full h-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${((subscription.dataUsage as Record<string, unknown>).isUnlimited || String(subscription.dataUsage.total) === 'Unlimited') ? 0 : subscription.dataUsage.percentage}%` }}
                  />
                </div>
              </div>

              {/* Status and End Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                <div>
                  <p className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Auto-Renew
                  </p>
                  <span className={`inline-block px-3 py-1 rounded text-sm font-bold ${
                    subscription.autoRenew
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}>
                    {subscription.autoRenew ? 'On' : 'Off'}
                  </span>
                </div>
                <div>
                  <p className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    End Date
                  </p>
                  <p className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString(): "N/A"}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-wrap">
                <button 
                  className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                  onClick={() => window.location.href = '/user/pricing'}
                >
                  {t('enduser.subscriptions.active.upgradeButton', { defaultValue: 'Upgrade Plan' })}
                </button>
                <button 
                  onClick={handleToggleAutoRenew}
                  disabled={actionLoading || subscription.status !== 'active'}
                  className={`w-full sm:w-auto px-6 py-2.5 rounded-lg font-bold transition-colors ${
                    theme === 'dark' 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' 
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {actionLoading
                    ? t('enduser.subscriptions.processing', { defaultValue: 'Processing...' })
                    : t('enduser.subscriptions.active.table.toggleAutorenewbutton', { defaultValue: 'Toggle Auto-Renew' })}
                </button>
                {subscription.status === 'active' && (
                  <button 
                    onClick={handleCancelSubscription}
                    className={`w-full sm:w-auto px-6 py-2.5 rounded-lg font-bold transition-colors ${
                      theme === 'dark' 
                        ? 'text-red-400 hover:text-red-300 border border-red-600 hover:bg-red-900/20' 
                        : 'text-red-600 hover:text-red-700 border border-red-300 hover:bg-red-50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {t('enduser.subscriptions.active.table.cancelbutton', { defaultValue: 'Cancel Subscription' })}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={`${theme === 'dark' ? 'bg-[#333436]' : 'bg-white'} rounded-xl p-4 sm:p-8 shadow-sm border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'} mb-8 text-center`}>
            <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              {t('enduser.subscriptions.noActive', { defaultValue: 'No active subscription' })}
            </p>
            <button 
              onClick={() => window.location.href = '/user/pricing'}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
            >
              Browse Plans
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-4 sm:gap-6 border-b border-gray-700 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setActiveTab('connection-history')}
              className={`pb-3 font-bold whitespace-nowrap transition-colors relative ${
                activeTab === 'connection-history'
                  ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'
              }`}
            >
              {t('enduser.subscriptions.connection.tabtitle', { defaultValue: 'Connection History' })}
              {activeTab === 'connection-history' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('devices')}
              className={`pb-3 font-bold whitespace-nowrap transition-colors relative ${
                activeTab === 'devices'
                  ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'
              }`}
            >
              {t('enduser.subscriptions.devices.tabtitle', { defaultValue: 'Devices' })}
              {activeTab === 'devices' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
          </div>
        </div>

        {/* Connection History */}
        {activeTab === 'connection-history' && (
          <div className={`${theme === 'dark' ? 'bg-[#333436]' : 'bg-white'} rounded-xl p-4 sm:p-8 shadow-sm border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="mb-6">
              <h2 className={`text-lg font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.subscriptions.connection.connectiontable.headertitle', { defaultValue: 'Connection History' })}
              </h2>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                All your WiFi sessions
              </p>
            </div>

            {existingHospot?.length > 0 ? (
              <>
              <div className="space-y-4">
                {existingHospot?.map((connection) => (
                  <ConnectionItem
                    key={String((connection as unknown as Record<string, unknown>)?.id)}
                    name={String(((connection as unknown as Record<string, unknown>)?.hotspot as Record<string, unknown>)?.name)}
                    duration={String((connection as unknown as Record<string, unknown>)?.duration)}
                    data={String((connection as unknown as Record<string, unknown>)?.dataUsed)}
                    date={new Date(String((connection as unknown as Record<string, unknown>)?.connectedAt)).toLocaleDateString()}
                    time={new Date(String((connection as unknown as Record<string, unknown>)?.connectedAt)).toLocaleTimeString()}
                    theme={theme}
                  />
                ))}
              </div>
    {haspagination === true ? <button
                      onClick={paginationFunc}
                      className={`w-full mt-4 py-3 text-sm font-medium rounded-lg border transition-colors flex justify-center ${
                        theme === 'dark'
                          ? 'text-gray-300 border-gray-600 hover:bg-gray-800'
                          : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {loadingpagination ? <Loader2 className="w-8 h-8 animate-spin text-blue-600" /> : t('enduser.subscriptions.loadMore', { defaultValue: 'Load More' })}
                    </button> : ""}
              </>

    ) : (
              <p className={`text-center py-12 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.subscriptions.connection.connectiontable.errorMessages.no_connection_found', { defaultValue: 'No connection history yet' })}
              </p>
            )}
      
          </div>
        )}

        {/* Devices */}
        {activeTab === 'devices' && (
          <div className={`${theme === 'dark' ? 'bg-[#333436]' : 'bg-white'} rounded-xl p-4 sm:p-8 shadow-sm border ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="mb-6">
              <h2 className={`text-lg font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {t('enduser.subscriptions.devices.tabtitle', { defaultValue: 'Connected Devices' })}
              </h2>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('enduser.subscriptions.devices.devicetable.headerbody', { defaultValue: 'Manage your connected devices' })}
              </p>
            </div>
            <p className={`text-center py-12 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('enduser.subscriptions.devices.errorMessages.no_device_found', { defaultValue: 'No devices connected' })}
            </p>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => {
          setShowFeedback(false);
          if (reloadOnFeedbackClose) {
            setReloadOnFeedbackClose(false);
            window.location.reload();
          }
        }}
        type={feedbackData.type}
        title={feedbackData.title}
        message={feedbackData.message}
      />

      {/* Cancel Confirmation Modal with Reason Input */}
      {showCancelConfirmation && subscription && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative w-full max-w-md rounded-xl shadow-2xl bg-white dark:bg-[#333436] animate-in zoom-in-95 duration-300">
            <div className="p-8">
              <div className="flex flex-col items-center text-center">
                {/* Icon */}
                <div className="p-4 rounded-full mb-6 bg-yellow-100 dark:bg-yellow-900/20">
                  <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
                  {t('enduser.subscriptions.cancelsubmodal.title', { defaultValue: 'Cancel Subscription?' })}
                </h3>

                {/* Message */}
                <p className="text-base text-gray-600 dark:text-gray-300 mb-4">
                  {t('enduser.subscriptions.cancelsubmodal.body', {
                    defaultValue: `Your subscription will remain active until ${subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'the end of the period'}.`,
                    date: subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'the end of the period'
                  })}
                </p>

                {/* Plan Name */}
                <div className="w-full p-4 mb-4 rounded-lg bg-gray-100 dark:bg-gray-800">
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {subscription.plan.name}
                  </p>
                </div>

                {/* Reason Input */}
                <div className="w-full mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                    {t('enduser.subscriptions.cancelsubmodal.body2', { defaultValue: 'Why are you cancelling? (Optional)' })}
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder={t('enduser.subscriptions.cancelsubmodal.inputplaceholder', { defaultValue: 'Help us improve by sharing your feedback...' })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-600 resize-none"
                    rows={3}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button
                    onClick={() => {
                      setShowCancelConfirmation(false);
                      setCancelReason('');
                    }}
                    disabled={actionLoading}
                    className="flex-1 w-full sm:w-auto px-6 py-3 rounded-lg font-medium bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('enduser.subscriptions.cancelsubmodal.keepsubscription', { defaultValue: 'Keep Subscription' })}
                  </button>
                  <button
                    onClick={confirmCancelSubscription}
                    disabled={actionLoading}
                    className="flex-1 w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {actionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('enduser.subscriptions.processing', { defaultValue: 'Processing...' })}
                      </>
                    ) : (
                      t('enduser.subscriptions.cancelsubmodal.cancelbutton', { defaultValue: 'Cancel Subscription' })
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ConnectionItem({ name, duration, data, date, time, theme }: { name: string, duration: string, data: string, date: string, time: string, theme: string }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0 p-4 sm:p-5 rounded-lg transition-colors ${
      theme === 'dark' ? 'bg-[#2a2b2d] hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
    }`}>
      <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
          <Wifi className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-bold text-base mb-1 truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {name}
          </h4>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 sm:gap-1.5">
              <Calendar className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-xs sm:text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {duration}
              </span>
            </div>
            <div className="flex items-center gap-1 sm:gap-1.5">
              <Database className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-xs sm:text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {data}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={`text-left sm:text-right pt-3 sm:pt-0 mt-2 sm:mt-0 w-full sm:w-auto border-t sm:border-t-0 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <p className={`font-bold text-base mb-0.5 sm:mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {date}
        </p>
        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {time}
        </p>
      </div>
    </div>
  );
}
